import * as React from 'react'
import {
  EditContextProvider,
  EditProps,
  SimpleForm,
  TextInput,
  SelectInput,
  NumberInput,
  DateInput,
  required,
  FormDataConsumer,
  Toolbar,
  SaveButton,
  useEditController,
  Record,
  ToolbarProps,
  DeleteButton,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Typography } from '@material-ui/core'
import { mainTheme } from '../../../utils/mainTheme'
import { useErrorHandler } from '../../../hooks/useCouponErrorHandler'
import LoadingNotification from '../../../components/CustomLayout/loading/LoadingNotification'
import HeaderWithGoBack from '../../../components/HeaderWithGoBack'

const CouponEditToolbar = (props: ToolbarProps) => {
  const notify = useNotify()
  const redirect = useRedirect()
  const { handleError } = useErrorHandler()

  const onSuccess = () => {
    notify('Cupom atualizado com sucesso')
    redirect('/coupon')
  }

  return (
    <Toolbar {...props}>
      <SaveButton
        onSuccess={onSuccess}
        onFailure={(error) => handleError(error, 'Erro ao editar cupom')}
        label="Salvar"
        style={{ marginRight: '20px' }}
      />
      {props.record.status != 'archived' && (
        <DeleteButton
          onSuccess={onSuccess}
          label="Arquivar"
          record={{ ...props.record, id: props.record.code }}
          mutationMode="pessimistic"
          confirmTitle={`Arquivar cupom #${props.record.code}`}
          confirmContent="Tem certeza que deseja arquivar esse cupom?"
        />
      )}
    </Toolbar>
  )
}

const validateDates = (value: Date, allValues: Record) => {
  if (allValues.validFrom && allValues.validUntil) {
    const from = new Date(allValues.validFrom)
    const until = new Date(allValues.validUntil)
    if (until <= from) return 'Data de fim deve ser posterior à data de início'
  }
  return undefined
}

const validateValue = (value: number, allValues: Record) => {
  if (allValues.type === 'percentage' && (value < 1 || value > 100)) {
    return 'Porcentagem deve estar entre 1 e 100'
  }
  if (allValues.type === 'fixed' && value < 0) {
    return 'Valor fixo deve ser maior ou igual a 0'
  }
  if (allValues.type === 'fixedPrice' && value < 0) {
    return 'Preço fixo deve ser maior ou igual a 0'
  }
  return undefined
}

const validateEligibleItems = (value: string[], allValues: Record) => {
  if (allValues.appliesTo === 'all_products' && value && value.length > 0) {
    return 'Itens elegíveis devem estar vazios quando aplica-se a todos os produtos'
  }
  if (
    allValues.appliesTo !== 'all_products' &&
    (!value || value.length === 0)
  ) {
    return 'Itens elegíveis são obrigatórios quando não aplica-se a todos os produtos'
  }
  return undefined
}

const CouponEdit = (props: EditProps) => {
  const controllerProps = useEditController({
    ...props,
    mutationMode: 'pessimistic',
  })
  const { record, loading, setTransform, save } = controllerProps

  if (loading || !record) {
    return <LoadingNotification />
  }

  const processedRecord = {
    ...record,
    validFrom: record.validFrom ? new Date(record.validFrom) : undefined,
    validUntil: record.validUntil ? new Date(record.validUntil) : undefined,
    eligibleItems: Array.isArray(record.eligibleItems)
      ? record.eligibleItems.join(',')
      : '',
  }

  const transformOnSave = (data: Record) => {
    return {
      ...data,
      eligibleItems:
        data.appliesTo === 'all_products' || !data.eligibleItems
          ? []
          : data.eligibleItems.split(',').map((s: string) => s.trim()),
    }
  }

  setTransform(transformOnSave)

  return (
    <EditContextProvider
      value={{
        ...controllerProps,
        record: processedRecord,
      }}
    >
      <SimpleForm
        toolbar={<CouponEditToolbar />}
        submitOnEnter={false}
        record={processedRecord}
        resource={props.resource}
        save={save}
      >
        <HeaderWithGoBack title="Editar cupom" />
        <br />
        {/* Código do Cupom - SOMENTE LEITURA */}
        <TextInput
          source="code"
          label="Código do Cupom"
          disabled
          helperText="Código não pode ser alterado após criação"
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: mainTheme.palette.text.secondary,
              backgroundColor: mainTheme.palette.grey[100],
            },
          }}
        />

        {/* Status - EDITÁVEL */}
        <SelectInput
          source="status"
          label="Status"
          choices={[
            { id: 'active', name: 'Ativo' },
            { id: 'inactive', name: 'Inativo' },
            { id: 'expired', name: 'Expirado' },
            { id: 'archived', name: 'Arquivado' },
          ]}
          validate={required()}
          fullWidth
          variant="outlined"
        />

        {/* Tipo de Desconto - EDITÁVEL */}
        <SelectInput
          source="type"
          label="Tipo de Desconto"
          choices={[
            { id: 'percentage', name: 'Porcentagem' },
            { id: 'fixed', name: 'Valor Fixo' },
            { id: 'fixedPrice', name: 'Preço Fixo' },
          ]}
          validate={required()}
          fullWidth
          variant="outlined"
        />

        {/* Valor - EDITÁVEL */}
        <FormDataConsumer>
          {({ formData }) => (
            <NumberInput
              source="value"
              label="Valor"
              validate={[required(), validateValue]}
              helperText={
                formData?.type === 'percentage'
                  ? 'Valor em porcentagem (1-100)'
                  : formData?.type === 'fixed'
                  ? 'Valor fixo em centavos (ex: 500 para R$5,00)'
                  : 'Preço final em centavos (ex: 8500 para R$85,00)'
              }
              fullWidth
              variant="outlined"
            />
          )}
        </FormDataConsumer>

        {/* Aplica-se a - EDITÁVEL */}
        <SelectInput
          source="appliesTo"
          label="Aplica-se a"
          choices={[
            { id: 'all_products', name: 'Todos os produtos' },
            { id: 'categories', name: 'Categorias específicas' },
            { id: 'specific_products', name: 'Produtos específicos' },
          ]}
          validate={required()}
          fullWidth
          variant="outlined"
        />

        {/* Itens Elegíveis - EDITÁVEL */}
        <FormDataConsumer>
          {({ formData }) =>
            formData?.appliesTo !== 'all_products' && (
              <TextInput
                source="eligibleItems"
                label="Itens Elegíveis"
                validate={validateEligibleItems}
                helperText={
                  formData?.appliesTo === 'categories'
                    ? 'Digite as categorias separadas por vírgula (ex: vaccine, earring)'
                    : 'Digite os SKUs dos produtos separados por vírgula'
                }
                fullWidth
                variant="outlined"
                defaultValue=""
              />
            )
          }
        </FormDataConsumer>

        {/* Data de Início - EDITÁVEL */}
        <DateInput
          source="validFrom"
          label="Válido de"
          validate={required()}
          fullWidth
          variant="outlined"
        />

        {/* Data de Fim - EDITÁVEL */}
        <DateInput
          source="validUntil"
          label="Válido até"
          validate={[required(), validateDates]}
          fullWidth
          variant="outlined"
        />

        {/* Limite de Uso - EDITÁVEL */}
        <SelectInput
          source="usageLimit"
          label="Limite de Uso"
          choices={[
            { id: 0, name: 'Ilimitado' },
            { id: 5, name: '5 usos' },
            { id: 10, name: '10 usos' },
            { id: 25, name: '25 usos' },
            { id: 50, name: '50 usos' },
            { id: 100, name: '100 usos' },
            { id: 500, name: '500 usos' },
            { id: 1000, name: '1000 usos' },
          ]}
          validate={[required()]}
          helperText="Selecione o limite de uso do cupom"
          fullWidth
          variant="outlined"
        />

        {/* Contador de Uso - SOMENTE LEITURA */}
        <NumberInput
          source="timesUsed"
          label="Vezes Usado"
          disabled
          helperText="Contador de uso (somente leitura)"
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: mainTheme.palette.text.secondary,
              backgroundColor: mainTheme.palette.grey[100],
            },
          }}
        />

        {/* Dados Externos - EDITÁVEL */}
        <FormDataConsumer>
          {() => (
            <div>
              <Typography variant="h2">Dados Externos (Opcional)</Typography>
              <TextInput
                source="externalData.doktorLink.medicoId"
                label="ID do Médico (Doktor Link)"
                helperText="Identificador único do médico no Doktor Link"
                fullWidth
                variant="outlined"
              />
              <TextInput
                source="externalData.doktorLink.campanhaId"
                label="ID da Campanha (Doktor Link)"
                helperText="Identificador único da campanha no Doktor Link"
                fullWidth
                variant="outlined"
              />
            </div>
          )}
        </FormDataConsumer>

        {/* Informações de Auditoria - SOMENTE LEITURA */}
        <div
          style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: mainTheme.palette.grey[50],
            borderRadius: '4px',
          }}
        >
          <h4
            style={{
              color: mainTheme.palette.text.primary,
              marginBottom: '10px',
            }}
          >
            Informações de Auditoria
          </h4>
          <TextInput
            source="createdAt"
            label="Data de Criação"
            disabled
            helperText="Data de criação (somente leitura)"
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: mainTheme.palette.text.secondary,
                backgroundColor: mainTheme.palette.grey[100],
              },
            }}
          />
          <TextInput
            source="updatedAt"
            label="Última Atualização"
            disabled
            helperText="Data da última atualização (somente leitura)"
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: mainTheme.palette.text.secondary,
                backgroundColor: mainTheme.palette.grey[100],
              },
            }}
          />
        </div>
      </SimpleForm>
    </EditContextProvider>
  )
}

export default CouponEdit
